import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export type VehicleTransmission = 'automatic' | 'manual'

export interface SchoolVehicle {
  id: string
  make: string
  model: string
  year: number | null
  colour: string | null
  kmRun: number | null
  licencePlate: string | null
  vin: string | null
  transmission: VehicleTransmission | null
  photoPath: string | null
  isActive: boolean
  assignedToName: string | null
  assignedToMembershipId: string | null
}

const PHOTO_BUCKET = 'vehicle-photos'

interface RpcResult {
  ok: boolean
  code?: string
  vehicle_id?: string
}

/**
 * A school's own fleet (migration 104) -- cars the school owns and assigns to its
 * EMPLOYEE instructors. Write path is RPC-gated (create_school_vehicle/
 * update_school_vehicle), not plain RLS, since photo_path needs server-side existence/
 * ownership validation the same way set_school_logo() already does for a school logo.
 */
export function useSchoolVehicles(schoolId: string | null) {
  const [vehicles, setVehicles] = useState<SchoolVehicle[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)

  const fetchAll = useCallback(async () => {
    if (!schoolId) { setVehicles([]); setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      // Fetch vehicles
      const { data: vehiclesData, error: fetchErr } = await supabase
        .from('school_vehicles')
        .select('id, make, model, year, colour, km_run, licence_plate, vin, transmission, photo_path, is_active')
        .eq('school_id', schoolId)
        .order('make', { ascending: true })

      if (fetchErr) { setError('Could not load the fleet.'); return }

      // Fetch assignments separately to find who each vehicle is assigned to
      const { data: membershipsData, error: membershipsErr } = await supabase
        .from('school_memberships')
        .select('assigned_vehicle_id, id, profiles!inner(full_name)')
        .eq('school_id', schoolId)
        .eq('status', 'active')
        .not('assigned_vehicle_id', 'is', null)

      if (membershipsErr) { setError('Could not load assignments.'); return }

      // Build a map of vehicle_id -> {membershipId, instructorName}
      const assignmentMap = new Map<string, { membershipId: string; instructorName: string }>()
      for (const m of (membershipsData ?? [])) {
        if (m.assigned_vehicle_id && (m.profiles as any)?.full_name) {
          assignmentMap.set(m.assigned_vehicle_id, {
            membershipId: m.id,
            instructorName: (m.profiles as any).full_name,
          })
        }
      }

      setVehicles((vehiclesData ?? []).map(v => {
        const assignment = assignmentMap.get(v.id)
        return {
          id: v.id,
          make: v.make,
          model: v.model,
          year: v.year,
          colour: v.colour,
          kmRun: v.km_run === null || v.km_run === undefined ? null : Number(v.km_run),
          licencePlate: v.licence_plate,
          vin: v.vin,
          transmission: v.transmission,
          photoPath: v.photo_path,
          isActive: v.is_active,
          assignedToName: assignment?.instructorName ?? null,
          assignedToMembershipId: assignment?.membershipId ?? null,
        }
      }))
    } finally {
      setLoading(false)
    }
  }, [schoolId])

  useEffect(() => { fetchAll() }, [fetchAll])

  const uploadVehiclePhoto = useCallback(async (file: File): Promise<{ ok: boolean; path?: string }> => {
    if (!schoolId || uploadingPhoto) return { ok: false }
    setUploadingPhoto(true)
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
      const path = `${schoolId}/${crypto.randomUUID()}.${ext}`
      const { error: upErr } = await supabase.storage.from(PHOTO_BUCKET).upload(path, file, { contentType: file.type, upsert: false })
      if (upErr) return { ok: false }
      return { ok: true, path }
    } finally {
      setUploadingPhoto(false)
    }
  }, [schoolId, uploadingPhoto])

  const photoUrl = useCallback((path: string | null) => {
    if (!path) return null
    return supabase.storage.from(PHOTO_BUCKET).getPublicUrl(path).data.publicUrl
  }, [])

  const createVehicle = useCallback(async (fields: {
    make: string; model: string; year?: number | null; colour?: string | null
    kmRun?: number | null; licencePlate?: string | null; vin?: string | null
    transmission?: VehicleTransmission | null; photoPath?: string | null
  }): Promise<RpcResult> => {
    if (!schoolId || saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('create_school_vehicle', {
        p_school_id: schoolId,
        p_make: fields.make,
        p_model: fields.model,
        p_year: fields.year ?? undefined,
        p_colour: fields.colour ?? undefined,
        p_km_run: fields.kmRun ?? undefined,
        p_licence_plate: fields.licencePlate ?? undefined,
        p_vin: fields.vin ?? undefined,
        p_transmission: fields.transmission ?? undefined,
        p_photo_path: fields.photoPath ?? undefined,
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as unknown as RpcResult
      if (result?.ok) await fetchAll()
      return result ?? { ok: false, code: 'internal_error' }
    } finally {
      setSaving(false)
    }
  }, [schoolId, saving, fetchAll])

  const updateVehicle = useCallback(async (vehicleId: string, fields: {
    make: string; model: string; year?: number | null; colour?: string | null
    kmRun?: number | null; licencePlate?: string | null; vin?: string | null
    transmission?: VehicleTransmission | null; photoPath?: string | null; isActive: boolean
  }): Promise<RpcResult> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('update_school_vehicle', {
        p_vehicle_id: vehicleId,
        p_make: fields.make,
        p_model: fields.model,
        p_year: fields.year ?? undefined,
        p_colour: fields.colour ?? undefined,
        p_km_run: fields.kmRun ?? undefined,
        p_licence_plate: fields.licencePlate ?? undefined,
        p_vin: fields.vin ?? undefined,
        p_transmission: fields.transmission ?? undefined,
        p_photo_path: fields.photoPath ?? undefined,
        p_is_active: fields.isActive,
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as unknown as RpcResult
      if (result?.ok) await fetchAll()
      return result ?? { ok: false, code: 'internal_error' }
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  const releaseVehicle = useCallback(async (membershipId: string): Promise<RpcResult> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('release_vehicle_assignment' as any, {
        p_school_membership_id: membershipId,
      } as any)
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as unknown as RpcResult
      if (result?.ok) await fetchAll()
      return result ?? { ok: false, code: 'internal_error' }
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  return {
    vehicles, loading, error, saving, uploadingPhoto,
    createVehicle, updateVehicle, releaseVehicle, uploadVehiclePhoto, photoUrl,
    refresh: fetchAll,
  }
}
